import * as companyBillService from "../../services/companyBill";
import * as customerBillService from "../../services/customerBill";
import * as settings from "../../services/settings";

import { Button, Col, Image, Row, Spin, Table, notification } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeleteOutlined } from "@ant-design/icons";
import { ModalCreateImages } from "./modal/ModalCreateImages";
import dayjs from "dayjs";
import permissions from "../../../configs/permission";
import { useAccessToken } from "../../contexts/AccessTokenContext";
import { useCallback } from "react";
import { usePermission } from "../../hooks/usePermission";
import { resolveImageUrl } from "../../utils/imageUrl";

const initPage = { limit: 5, page: 1, total: 1 };
export const CreateImageBill = ({ isCompanyBill, companyBillItemId, translations }) => {
  const { t } = useTranslation(translations);
  const hasFetchedRef = useRef(false);
  const { hasPermission, isSuperuser } = usePermission();
  const { accessToken } = useAccessToken();
  const [loading, setLoading] = useState(false);
  const [imageData, setImageData] = useState([]); // To keep track of current data in the table
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(initPage);
  const [api, contextHolder] = notification.useNotification();
  const [isOpen, setIsOpen] = useState(false);
  const openNotificationWithIcon = (type, message, description) => {
    api[type]({
      message: message,
      description: description,
    });
  };

  const fetchData = useCallback(
    async (param) => {
      if (!accessToken) return;
      setLoading(true);
      try {
        if (isCompanyBill) {
          const response = await companyBillService.getCompanyBillImageList(accessToken, param);
          setImageData(response?.queryCompanyBillImage);
          setTotal(response?.totalCount);
          //fetch company image
        } else {
          param.customer_bill_no = param.companyBillItemId;
          const response = await customerBillService.getCompanyBillImageList(accessToken, param);
          setImageData(response?.queryCustomerBillImage);
          setTotal(response?.totalCount);
        }
      } catch (err) {
        console.error("[CreateImageBill] fetchData: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, "ERROR.", `${message}`);
      } finally {
        setLoading(false);
      }
    },
    [accessToken]
  );

  const onSearch = useCallback(
    async (value = {}) => {
      const data = {
        page: value.page ?? search.page ?? initPage.page,
        limit: value.limit ?? search.limit ?? initPage.limit,
        ...(isCompanyBill ? { company_bill_id: companyBillItemId } : { customer_bill_id: companyBillItemId }),
      };

      setSearch((prev) => ({
        ...prev,
        ...data,
      }));

      await fetchData(data);
    },
    [search.page, search.limit, isCompanyBill, companyBillItemId, fetchData]
  );

  const onDelete = async (record) => {
    setLoading(true);
    try {
      if (isCompanyBill) {
        let prepareData = {
          id: record.id,
          company_bill_id: companyBillItemId,
        };
        const response = await companyBillService.deleteCompanyBillImage(accessToken, prepareData);
      } else {
        let prepareData = {
          id: record.id,
          customer_bill_id: companyBillItemId,
        };
        const response = await customerBillService.deleteCustomerBillImage(accessToken, prepareData);
      }

      openNotificationWithIcon(settings.NotificationType.SUCCESS, "Success.", "Delete Image success");

      // Remove deleted item from the data list
      // setImageData((prevData) => prevData.filter((item) => item.id !== record.id));
      onSearch();
    } catch (err) {
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, "ERROR.", `${message}`);
      console.error("[CreateImageBill] onDelete", err);
    } finally {
      setLoading(false); // Ensure loading is stopped
    }
  };

  const openModal = () => {
    setIsOpen(true);
  };

  const handleOk = () => {
    setIsOpen(false);
  };
  const handleCancel = () => {
    setIsOpen(false);
  };

  const columns = [
    {
      title: "ID",
      width: 100,
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Image",
      width: 300,
      dataIndex: "image",
      key: "image",
    },
    {
      title: "Thumbnail",
      width: 300,
      dataIndex: "thumbnail",
      key: "thumbnail",
      render: (_, record) => {
        const imagePath = record.thumbnail ?? record.image;
        const src = imagePath != null && imagePath !== "" ? resolveImageUrl(imagePath) : "default_image_url";

        return (
          <Image src={src} alt="Thumbnail" width={300} height={300} style={{ objectFit: "cover" }} preview={{ src }} />
        );
      },
    },
    {
      title: "Created At",
      width: 150,
      dataIndex: "created_at",
      key: "created_at",
      render: (_, record) => {
        const created_at = record.created_at != null ? dayjs(record.created_at).format("YYYY-MM-DD HH:mm:ss") : "-";
        return <p>{created_at}</p>;
      },
    },
    {
      title: "Delete",
      width: 150,
      dataIndex: "delete",
      key: "delete",
      render: (_, record) => {
        return (
          <Button
            // disabled={isCompanyBill ? true : false}
            type="link"
            onClick={() => onDelete(record)}
          >
            <DeleteOutlined />
          </Button>
        );
      },
    },
  ];

  useEffect(() => {
    if (!accessToken || hasFetchedRef.current) return;
    onSearch({ ...initPage });
    hasFetchedRef.current = true;
  }, [accessToken, onSearch]);

  return (
    <>
      {contextHolder}
      <Spin spinning={loading}>
        <Row align="middle" justify="end">
          <Col span={24}>
            <Table
              loading={loading}
              columns={columns}
              dataSource={Array.isArray(imageData) ? imageData : []}
              scroll={{ x: "1440px" }}
              rowKey={(record) => record.id}
              style={{ cursor: "pointer" }}
              pagination={{
                current: Number(search.page),
                pageSize: Number(search.limit),
                total,
                pageSizeOptions: ["5", "10", "20", "50", "100"],
                showSizeChanger: true,
                onChange: (page, pageSize) => {
                  onSearch({
                    page,
                    limit: pageSize,
                  });
                },
              }}
            />
          </Col>
          {hasPermission(isCompanyBill ? permissions.CompanyBillImage.ADD : permissions.CustomerBillImage.ADD) && (
            <>
              <Row align="middle" justify="end">
                <Col span={24} style={{ marginTop: "10px", justifyContent: "end" }}>
                  <Button type="primary" onClick={openModal}>
                    {t("Button_Customer_Bill.BTN_Create", { ns: "customerbill" })}
                  </Button>
                </Col>
              </Row>
            </>
          )}
        </Row>

        <ModalCreateImages
          isCompanyBill={isCompanyBill}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          handleCancel={handleCancel}
          handleOk={handleOk}
          companyBillItemId={companyBillItemId}
          onSearch={onSearch}
        />
      </Spin>
    </>
  );
};
